import React, { ReactNode } from 'react';
import { PortableText, PortableTextReactComponents } from '@portabletext/react';
import FAQItem from '../../faqItem/FAQItem';
import PortableBonusCard from '../../portableBonusCard/PortableBonusCard';
import styles from './PortableBlock.module.scss';
import NextImage from '../nextImage/NextImage';
import imageHelper from '@/src/assets/helpers/imageHelper';
import TableRow from '../tableRow/TableRow';
import { ITableRow } from '@/src/assets/interfaces/tableInterface';

const portableCaption: Partial<PortableTextReactComponents> = {
  block: {
    normal: ({ children }) => {
      return (
        <figcaption className={styles.caption__wrapper}>
          <p className={styles.caption__p}>{children}</p>
        </figcaption>
      );
    }
  },
  marks: {
    strong: ({ children }: { children: ReactNode }) => <strong>{children}</strong>,
    em: ({ children }: { children: ReactNode }) => <em>{children}</em>
  }
};

const PortableImage = ({ value }: any) => {
  const hasSizeOption = !!value.imageSize || !!value.customImageSize;
  const isSVG = !!value?.asset?._ref.endsWith('svg');
  let maxImageSize = isSVG ? '400' : '1184';

  if (!value.asset) {
    return '';
  }

  if (value.imageSize && value.imageSize !== 'default') {
    maxImageSize = value.imageSize;
  }

  if (value.customImageSize) {
    maxImageSize = value.customImageSize.toString();
  }

  const inlineStyles = hasSizeOption
    ? {
        width: '100%',
        maxWidth: `${maxImageSize}px`
      }
    : undefined;

  return (
    <figure className={styles.image__container}>
      {value && (
        <NextImage
          src={value && imageHelper(value).url}
          alt={imageHelper(value).alt ?? 'Image description'}
          className={`${styles.postContentImage} ${isSVG && styles.svgImage}`}
          fill={true}
          loading={'lazy'}
          sizes={`(max-width: 768px) 100vw, ${maxImageSize}px`}
          style={inlineStyles}
        />
      )}
      {value?.caption && <PortableText value={value?.caption} components={portableCaption} />}
    </figure>
  );
};

const listItemClass = ({ children }: any) => {
  if (children && children[1] && '_type' in children[1]) {
    if (children[1]._type === 'bonusLink') {
      return styles.bonus__link;
    } else if (children[1]._type === 'faqLink') {
      return styles.faqLink;
    } else return undefined;
  } else {
    return undefined;
  }
};

const serializers: Partial<PortableTextReactComponents> = {
  types: {
    image: ({ value }) => {
      return <PortableImage value={value} />;
    },
    table: ({ value }) => {
      return (
        <div className={styles.portable__wrapper}>
          {value.rows && (
            <table className={styles.portable__table}>
              <tbody>
                {value.rows.map((row: ITableRow) => (
                  <TableRow key={row._key} row={row} />
                ))}
              </tbody>
            </table>
          )}
        </div>
      );
    },
    faqLink: ({ value }) => (value ? <FAQItem item={value} /> : null),
    bonusLink: ({ value }) => (value ? <PortableBonusCard cardData={value} /> : null)
  },
  block: {
    h2: ({ children, value }) => {
      return <h2 id={value._key}>{children}</h2>;
    },
    blockquote: ({ children }) => {
      return <blockquote className={styles.portable__blockquote}>{children}</blockquote>;
    },
    h3: ({ children, value }) => {
      return <h3 id={value._key}>{children}</h3>;
    },
    normal: ({ children }: any) => {
      const bonusItem = children.find((item: any) => item?.type?.name === 'bonusLink');

      if (bonusItem) {
        return (
          <div className={`${styles.portable__wrapper} ${styles.bonus__link}`}>
            <PortableBonusCard cardData={bonusItem.props?.value} />
          </div>
        );
      }
      return <p>{children}</p>;
    }
  },
  marks: {
    strong: ({ children }: { children: ReactNode }) => <strong>{children}</strong>,
    em: ({ children }: { children: ReactNode }) => <em>{children}</em>,
    li: ({ children }: { children: ReactNode }) => <li>{children}</li>,
    link: ({ value, children }) => {
      const target = value.blank ? '_blank' : undefined;
      return (
        <a href={value.url} target={target} rel={target ? 'noopener noreferrer' : undefined}>
          {children}
        </a>
      );
    }
  },
  list: {
    bullet: ({ children }) => <ul>{children}</ul>,
    number: ({ children }) => <ol>{children}</ol>
  },
  listItem: {
    bullet: ({ children, value }) => {
      return (
        <li
          className={`portable__list-item ${listItemClass(value) ?? styles.marked__link}`}
          style={{ listStyleType: 'none', listStylePosition: 'inside' }}>
          {children}
        </li>
      );
    },

    number: ({ index, children, value }) => {
      const listItemIndex = index + 1;
      return (
        <li
          className={`number__list-item ${listItemClass(value) ?? styles.numbered__list}`}
          style={{ listStyleType: 'none', listStylePosition: 'inside' }}>
          <span>{listItemClass(value) ? listItemIndex : ''}</span>
          {children}
        </li>
      );
    }
  }
};

const PortableBlock: React.FC<any> = ({ value }) => {
  return <PortableText value={value} components={serializers} />;
};

export default PortableBlock;
